import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ShoppingBag, 
  Filter,
  Star,
  Clock,
  Dumbbell,
  Loader2
} from 'lucide-react';

export default function MarketplacePage() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    fetchItems();
  }, [token, filter]);

  const fetchItems = async () => {
    try {
      const params = filter ? `?category=${filter}` : '';
      const response = await axios.get(`${API}/marketplace${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching marketplace:', error);
    } finally {
      setLoading(false);
    }
  };

  const purchaseItem = async (itemId) => {
    setPurchasing(itemId);
    try {
      const response = await axios.post(
        `${API}/marketplace/purchase?item_id=${itemId}&origin_url=${encodeURIComponent(window.location.origin)}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.success('Item added to your library!');
      }
    } catch (error) {
      toast.error('Failed to process purchase');
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="marketplace-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
            <span className="text-primary">MARKETPLACE</span>
          </h1>
          <p className="text-gray-400 mt-1">Premium workout programs and coaching</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48 bg-secondary border-transparent">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="strength">Strength</SelectItem>
            <SelectItem value="cardio">Cardio</SelectItem>
            <SelectItem value="hiit">HIIT</SelectItem>
            <SelectItem value="yoga">Yoga</SelectItem>
            <SelectItem value="sports">Sports</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Items Grid */}
      {items.length === 0 ? (
        <div className="text-center py-12 card-tactical">
          <ShoppingBag className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl text-white mb-2">No programs available yet</h3>
          <p className="text-gray-400">Check back later for premium workout programs!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <div key={item.id} className="card-tactical group">
              <div className="flex items-start justify-between mb-4">
                <div className={`px-2 py-1 rounded-sm text-xs uppercase font-semibold ${
                  item.difficulty === 'beginner' ? 'text-green-500 bg-green-500/10' :
                  item.difficulty === 'intermediate' ? 'text-yellow-500 bg-yellow-500/10' :
                  'text-red-500 bg-red-500/10'
                }`}>
                  {item.difficulty}
                </div>
                <div className="flex items-center gap-1 text-yellow-500">
                  <Star className="w-4 h-4 fill-current" />
                  <span className="text-sm">4.9</span>
                </div>
              </div>
              
              <h3 className="font-heading text-xl text-white mb-2">{item.title}</h3>
              <p className="text-gray-400 text-sm mb-4 line-clamp-2">{item.description}</p>
              
              <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {item.duration_weeks} weeks
                </div>
                <div className="flex items-center gap-1">
                  <Dumbbell className="w-4 h-4" />
                  {item.exercises?.length || 0} exercises
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="font-mono text-2xl font-bold text-primary">
                  ${item.price?.toFixed(2) || 'Free'}
                </div>
                <Button
                  data-testid={`purchase-${item.id}`}
                  onClick={() => purchaseItem(item.id)}
                  disabled={purchasing === item.id}
                  className="bg-primary hover:bg-red-700"
                >
                  {purchasing === item.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Get Program'
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
